import React from 'react';
import { IGeoLocation, IImage } from 'utils/types';
import { motion, useInView } from 'framer-motion';

import s from './PipelineMap.module.scss';

type Props = {
  map?: IImage;
  approvals?: {
    name: string;
    location?: IGeoLocation;
    label?: string;
  }[];
};

export const PipelineMap = ({ map, approvals }: Props) => {
  const mapRef = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(mapRef, { once: false, margin: '-25%' });

  const calculatePositions = (latitude, longitude) => {
    if (latitude === undefined || longitude === undefined) {
      console.warn('Please provide both latitude and longitude!');
      return;
    }

    // Convert latitude and longitude to positions as percentages
    const topPercentage = ((115 - latitude * 0.95) / 180) * 95; // Latitude: -90 (bottom) to 90 (top)
    const leftPercentage = ((longitude * 0.95 + 180) / 360) * 94; // Longitude: -180 (left) to 180 (right)

    return {
      top: `${parseFloat(topPercentage.toFixed(2))}%`,
      left: `${parseFloat(leftPercentage.toFixed(2))}%`,
    };
  };

  // stagger the markers
  var mapVariants = {
    hidden: { opacity: 1 },
    visible: { opacity: 1, transition: { staggerChildren: 0.25 } },
  };

  var markerVariants = {
    hidden: { opacity: 0, x: '-50%', y: -50, rotate: 45 },
    visible: { opacity: 1, x: '-50%', y: 0, rotate: 45 },
  };

  var shadowVariants = {
    hidden: { scale: 0 },
    visible: { scale: 1 },
  };

  var wrapVariants = {
    hidden: { scale: 1.2 },
    visible: { scale: 1 },
  };

  return (
    <motion.div
      className={s.map}
      ref={mapRef}
      variants={mapVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      {!!map && (
        <img
          src={map.url}
          alt="Pipeline Map"
          className={s.view__image}
          width={map.width}
          height={map.height}
        />
      )}
      {!map && (
        <img
          src="/static/images/pipeline/blank-map.svg"
          alt="Pipeline Map"
          className={s.view__image}
        />
      )}
      {approvals.map((approval, index) => {
        if (!approval.location) {
          return null; // Skip if location is not provided
        }

        return (
          <motion.div
            key={index}
            className={s.map__location}
            style={calculatePositions(approval.location?.latitude, approval.location?.longitude)}
          >
            <div className={s.map__pointer} />
            <motion.div variants={wrapVariants}>
              <motion.div className={s.map__shadow} variants={shadowVariants} />
              <motion.div className={s.map__marker} variants={markerVariants} />
            </motion.div>
            {(approval.name || approval.label) && (
              <motion.div className={s.map__label}>{approval.label || approval.name}</motion.div>
            )}
          </motion.div>
        );
      })}
    </motion.div>
  );
};
